import { toast } from "saphir";

import { isAccessNotReadyError, isAuthenticationRequiredError } from "@lib/access/access";
import { setAccessErrorHandler } from "@lib/access/authRedirect";
import {
    getResolvedAccessRedirectPathname,
    markAccessRedirectPending,
} from "@lib/access/redirectNotice";
import { clearScopeInsiderCapability, resolveTeamScopeContext } from "@lib/api/scopeContext";
import env, { ENVIRONMENT } from "@lib/env";
import { i18n, i18nDK } from "@lib/i18n";
import { clearLogas } from "@lib/utils/clearLogas";
import isAuth from "@lib/utils/isAuth";
import { router } from "@routes";
import {
    RequestError,
    resolveScopeBinding,
    ResponseError,
    ScopeContextChangedError,
    setSpellbookConfig,
    type SpellbookError,
    type SpellbookErrorContext,
} from "@sdk/spellbook";
import { connectSpellbookToQueryClient } from "@sdk/tanstack";
import * as Sentry from "@sentry/react";
import type { QueryClient } from "@tanstack/react-query";

import { clearLogin } from "./src/lib/utils/clearLogin";

let errorQueryClient: QueryClient | undefined;
let accessRedirectInProgress = false;
// Direct SDK calls report immediately; if TanStack later sees the same error,
// keep the final exception capture without repeating that feedback.
const apiErrorsAwaitingCacheCapture = new WeakSet<SpellbookError>();

/** Connects the app's QueryClient and makes it available for auth recovery. */
export function configureSpellbookErrorPolicy(queryClient: QueryClient): () => void {
    errorQueryClient = queryClient;
    const disconnect = connectSpellbookToQueryClient(queryClient);

    return () => {
        disconnect();
        if (errorQueryClient === queryClient) errorQueryClient = undefined;
    };
}

function redirectToAccessFallback(): void {
    if (accessRedirectInProgress) return;

    const sourcePath = getResolvedAccessRedirectPathname() ?? router.state.location.pathname;
    if (!markAccessRedirectPending(sourcePath)) return;

    accessRedirectInProgress = true;
    void router.navigate({ to: "/", replace: true }).finally(() => {
        accessRedirectInProgress = false;
    });
}

function recoverFromAuthenticationRejection(error: unknown): void {
    const isSpellbookResponse401 = error instanceof ResponseError && error.response.status === 401;
    errorQueryClient?.removeQueries({ queryKey: ["user"] });
    if (!isSpellbookResponse401 && !isAuth()) return;

    clearLogin();
    redirectToAccessFallback();
}

function recoverFromCurrentUserContextRejection(
    error: ResponseError,
    operation: SpellbookErrorContext["operation"] | undefined,
): void {
    if (operation?.method !== "GET") return;
    if (operation.pathTemplate !== "/v1/users/me") return;
    if (error.response.status < 400) return;
    if (!error.bodyParsed) return;

    const currentBinding = resolveScopeBinding(operation).binding;
    if (error.scopeBinding.usesResolver !== currentBinding.usesResolver) return;
    if (error.scopeBinding.status !== currentBinding.status) return;
    if (
        JSON.stringify(error.scopeBinding.cacheIdentity) !==
        JSON.stringify(currentBinding.cacheIdentity)
    ) {
        return;
    }

    // Preserve the prior current-user recovery contract until Team API returns
    // an explicit recovery code. This clears scope state without claiming why
    // the lookup failed; future recovery should use that explicit code.
    clearLogas();
    clearScopeInsiderCapability();
    errorQueryClient?.removeQueries({ queryKey: ["user", "detail"] });
    redirectToAccessFallback();
}

function reportApiError(
    error: SpellbookError,
    context: Pick<SpellbookErrorContext, "source">,
): void {
    if (context.source === "call") {
        // Legacy query functions use createCall without managed query metadata.
        // The SDK reports immediately; defer their final capture to QueryCache.
        apiErrorsAwaitingCacheCapture.add(error);
        return;
    }

    Sentry.captureException(error);
    console.error(error);
}

/** Captures the final legacy SDK failure once after its query retries finish. */
export function reportLegacyApiCacheError(error: SpellbookError): void {
    if (!apiErrorsAwaitingCacheCapture.has(error)) return;

    apiErrorsAwaitingCacheCapture.delete(error);
    Sentry.captureException(error);
    console.error(error);
}

function addResponseErrorBreadcrumb(
    error: ResponseError,
    level: "warning" | "error",
    timestamp: number,
): void {
    Sentry.addBreadcrumb({
        level,
        category: error.reason,
        message: error.message,
        data: {
            status: `${error.response.status} ${error.response.statusText}`,
            ...(error.reason === "StatusCode" ? { serverMsg: error.serverMsg } : {}),
            extra: error.extra,
            cause: error.cause,
        },
        timestamp,
    });
}

/** Applies the one generic API-error policy for Spellbook and legacy access errors. */
export function handleApiError(
    error: unknown,
    context: Pick<SpellbookErrorContext, "source"> &
        Partial<Pick<SpellbookErrorContext, "operation">>,
): void {
    if (error instanceof ScopeContextChangedError) return;

    const timestamp = Date.now() / 1000;
    if (isAuthenticationRequiredError(error)) {
        if (error instanceof ResponseError) {
            addResponseErrorBreadcrumb(error, "warning", timestamp);
        }
        recoverFromAuthenticationRejection(error);
        return;
    }

    if (isAccessNotReadyError(error)) {
        if (context.source === "query") {
            redirectToAccessFallback();
        } else {
            toast.error(i18n.errors_action_context_unavailable());
        }
        return;
    }

    if (error instanceof RequestError) {
        if (error.reason === "Abort") {
            Sentry.addBreadcrumb({
                level: "info",
                category: error.reason,
                message: error.message,
                timestamp,
            });
            return;
        }

        if (error.reason === "Encode") toast.error(i18n.errors_encode());
        if (error.reason === "Transport") toast.error(i18n.errors_transport());

        Sentry.addBreadcrumb({
            level: "error",
            category: error.reason,
            message: error.message,
            data: {
                extra: error.extra,
                cause: error.cause,
            },
            timestamp,
        });
        reportApiError(error, context);
        return;
    }

    if (!(error instanceof ResponseError)) return;

    if (error.reason === "StatusCode") {
        recoverFromCurrentUserContextRejection(error, context.operation);
        addResponseErrorBreadcrumb(error, "warning", timestamp);

        if (error.serverMsg) {
            const key = error.serverMsg.replaceAll(".", "_");
            toast.error(i18nDK(key, { defaultValue: error.serverMsg }));
        } else {
            toast.error(i18n.errors_generic());
        }
        reportApiError(error, context);
        return;
    }

    addResponseErrorBreadcrumb(error, "error", timestamp);
    if (error.reason === "Decode" || error.reason === "EmptyBody") {
        toast.error(i18n.errors_generic());
    }
    reportApiError(error, context);
}

Sentry.init({
    dsn: "https://44aa6ead597f429980114d33c5305b72@sentry.io/211065",
    environment: ENVIRONMENT,
    // Adds request headers and IP for users, for more info visit:
    // https://docs.sentry.io/platforms/javascript/guides/react/configuration/options/#sendDefaultPii
    sendDefaultPii: true,
    // Overwrite the defaults to ensure no trace headers are sent
    tracePropagationTargets: [],
    tracesSampleRate: undefined,
    tracesSampler: undefined,
    beforeBreadcrumb: (breadcrumb) => {
        // filter out unleash "info" level requests
        if (
            breadcrumb.category === "fetch" &&
            breadcrumb.data?.url?.includes("unleash.side.co") &&
            breadcrumb.level === undefined
        ) {
            return null;
        }
        return breadcrumb;
    },
});

Sentry.setUser({
    id:
        (localStorage.getItem(`side_team_logasId`) || localStorage.getItem(`Meteor.userId`)) ??
        undefined,
});

setSpellbookConfig({
    baseUrl: `${env.API_URL}${env.DOMAIN}`,
    resolveScopeContext: resolveTeamScopeContext,
    fetch: (request) => fetch(new Request(request, { credentials: "omit" })),
    onError: handleApiError,
});

setAccessErrorHandler((error, source) => handleApiError(error, { source }));
